import React, { useState } from 'react';
import styled from 'styled-components';
import Panel from './Panel';
import PeriodSelector from './PeriodSelector';
import useMonitoringData from '../hooks/useMonitoring';
import { fetchErrorSummary, fetchErrorLogs } from '../api/monitoringApi';

const Tabs = styled.div`display: flex; gap: 0; margin-bottom: 12px; border-bottom: 1px solid #e5e7eb;`;
const Tab = styled.button`
  padding: 8px 16px; border: none; background: none; font-size: 13px; cursor: pointer;
  color: ${p => p.$active ? '#667eea' : '#6b7280'}; font-weight: ${p => p.$active ? 600 : 400};
  border-bottom: 2px solid ${p => p.$active ? '#667eea' : 'transparent'};
`;

const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 13px;`;
const Th = styled.th`text-align: left; padding: 6px 8px; background: #f9fafb; border-bottom: 1px solid #e5e7eb; color: #6b7280; font-weight: 600;`;
const Td = styled.td`padding: 6px 8px; border-bottom: 1px solid #f3f4f6; color: #374151;`;
const ErrorCount = styled.span`font-size: 20px; font-weight: 700; color: #ef4444;`;
const Section = styled.div`margin-top: 12px;`;
const SectionTitle = styled.div`font-size: 13px; font-weight: 600; color: #374151; margin-bottom: 6px;`;

function fmtTime(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function OverviewTab({ data }) {
  if (!data) return null;
  return (
    <>
      <div style={{ marginBottom: 16 }}>총 에러: <ErrorCount>{data.total_errors}</ErrorCount></div>
      <Section>
        <SectionTitle>Top 10 에러 엔드포인트</SectionTitle>
        <Table>
          <thead><tr><Th>Method</Th><Th>URL</Th><Th>상태코드</Th><Th>건수</Th></tr></thead>
          <tbody>{data.by_endpoint?.map((r, i) => (
            <tr key={i}><Td>{r.method}</Td><Td>{r.url}</Td><Td>{r.result_code}</Td><Td>{r.count}</Td></tr>
          ))}</tbody>
        </Table>
      </Section>
      <Section>
        <SectionTitle>Top 10 에러 고객</SectionTitle>
        <Table>
          <thead><tr><Th>고객</Th><Th>이메일</Th><Th>건수</Th></tr></thead>
          <tbody>{data.by_client?.map((r, i) => (
            <tr key={i}><Td>{r.client_name || '-'}</Td><Td>{r.client_email || '-'}</Td><Td>{r.count}</Td></tr>
          ))}</tbody>
        </Table>
      </Section>
    </>
  );
}

function LogsTab({ data }) {
  if (!data) return null;
  return (
    <div style={{ overflowX: 'auto' }}>
      <Table>
        <thead><tr><Th>시간</Th><Th>Method</Th><Th>URL</Th><Th>상태</Th><Th>고객</Th><Th>소요</Th></tr></thead>
        <tbody>
          {data?.map((r, i) => (
            <tr key={i}>
              <Td>{fmtTime(r.timestamp)}</Td><Td>{r.method}</Td><Td>{r.url}</Td>
              <Td style={{color:'#ef4444'}}>{r.result_code}</Td>
              <Td>{r.client_name || '-'}</Td><Td>{r.run_time_ms}ms</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export default function ErrorTrackingPanel({ refreshKey }) {
  const [period, setPeriod] = useState('24h');
  const [tab, setTab] = useState('overview');
  const summary = useMonitoringData(() => fetchErrorSummary(period), [period, refreshKey]);
  const logs = useMonitoringData(() => fetchErrorLogs(period, 50), [period, refreshKey]);

  const isLoading = tab === 'overview' ? summary.isLoading : logs.isLoading;
  const error = tab === 'overview' ? summary.error : logs.error;

  return (
    <Panel title="에러 추적" isLoading={isLoading} error={error}
      actions={<PeriodSelector value={period} onChange={setPeriod} />}>
      <Tabs>
        <Tab $active={tab === 'overview'} onClick={() => setTab('overview')}>개요</Tab>
        <Tab $active={tab === 'logs'} onClick={() => setTab('logs')}>로그</Tab>
      </Tabs>
      {tab === 'overview' ? <OverviewTab data={summary.data} /> : <LogsTab data={logs.data} />}
    </Panel>
  );
}
